// frontend/src/widgets/OpportunityStageProgressWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-09 10:48 BST
//
// Description:
//   Stage / status progress bar for Opportunities page (top row).
//   Allows stage + status changes when canEdit=true.
//
// Fixes (UTC):
//   • Replaces fragile pseudo-element chevrons with robust clip-path chevron segments.
//   • Deterministic visible gap between stages via --vx-gap (no overlap/stacking hacks).
//   • No “diamond/triangle” artifacts (no rotated squares).
//   • Current stage uses theme.primary_color; blocked uses theme.secondary_color (amber fallback).
//   • Preserves all existing logic + tooltips + actions.
//
// Patch (2026-01-21):
//   • DB enum alignment: Discovery is NOT a stage value (enum uses needs_analysis).
//   • Canonicalise stage values:
//       discovery -> needs_analysis
//     so Mark stage complete cannot silently attempt invalid enum writes.
//
// Mobile Responsiveness (2026-09-09):
//   • Desktop retains the existing left-to-right stage progression.
//   • Mobile (<=767px) presents the same stages vertically, top to bottom.
//   • The first mobile pane shows the complete stage progression in full.
//   • Mobile height growth uses the proven Vision list-widget pattern:
//     restore any prior host override, clear temporary widget/pager sizing,
//     measure the natural pane content, then resize the first matching fixed
//     mobile host from the widget's actual rendered root height.
//   • The shared pane height is the larger of Stage Progress or Next step/actions
//     so neither vertical pane can be clipped.
//   • A natural vertical swipe/scroll moves down to the existing Fix required /
//     Next step panel and action controls; scrolling back up returns to stages.
//   • No swipe instruction text, paging buttons or horizontal carousel is used.
//   • Mobile stages are compact: 48px high with 11px labels and smaller arrow
//     geometry so the progression remains readable without becoming clunky.
//   • No stage/status callbacks, blocker logic, canonicalisation, keyboard
//     actions or tooltip event handlers are changed.
//
// Operational Notes:
//   • Mobile pane navigation uses native vertical scroll-snap only.
//   • No duplicate mobile/desktop render trees are mounted.
//   • Existing desktop/tablet behaviour is preserved at >=768px.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext.jsx";

const norm = (v) => String(v || "").trim().toLowerCase();

const titleCase = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ------------------------
// Stage canonicalisation + labels (must match DB enum values)
// ------------------------
const STAGE_ALIASES = {
  discovery: "needs_analysis",
  analysis: "needs_analysis",
  needsanalysis: "needs_analysis",
  verbalcommit: "verbal_commit",
};

const STAGE_LABELS = {
  qualification: "Qualification",
  needs_analysis: "Discovery",
  proposal: "Proposal",
  negotiation: "Negotiation",
  verbal_commit: "Verbal Commit",
  closed: "Closed",
};

const canonicalStage = (s) => {
  const k = norm(s).replace(/\s+/g, "_");
  return STAGE_ALIASES[k] || k;
};

const stageLabel = (s) => {
  const k = canonicalStage(s);
  return STAGE_LABELS[k] || titleCase(k);
};

const normalizeStages = (stages) => {
  const arr = Array.isArray(stages) ? stages : [];
  const out = [];
  const seen = new Set();
  for (const s of arr) {
    const k = canonicalStage(s);
    if (!k) continue;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
};

const idxOfStage = (stages, stage) => {
  const target = canonicalStage(stage);
  const i = (stages || []).findIndex((s) => canonicalStage(s) === target);
  return i >= 0 ? i : 0;
};

const getNextStage = (stages, currentIdx) => {
  if (!Array.isArray(stages) || stages.length === 0) return null;
  const ni = Math.min(stages.length - 1, Math.max(0, currentIdx + 1));
  return stages[ni] || null;
};

const buildBlockers = (stages, opportunity) => {
  const o = opportunity || {};
  const stage = canonicalStage(o.stage);
  const status = norm(o.status);

  const blockers = [];

  if (!String(o.name || "").trim())
    blockers.push({ key: "name", title: "Set a name", detail: "Opportunity name is required." });

  if (stage === "qualification") {
    if (!o.owner_resource_id)
      blockers.push({ key: "owner", title: "Assign an owner", detail: "Owner resource is required to progress." });
    if (!o.primary_contact_id)
      blockers.push({
        key: "primary_contact",
        title: "Set a primary contact",
        detail: "Primary contact is required for comms and accountability.",
      });
  }

  // ✅ Discovery == needs_analysis in DB enum
  if (stage === "needs_analysis") {
    if (!o.expected_close_date)
      blockers.push({
        key: "close_date",
        title: "Set expected close date",
        detail: "Expected close date is required for pipeline forecasting.",
      });
    if (o.probability === null || o.probability === undefined || String(o.probability).trim() === "")
      blockers.push({
        key: "probability",
        title: "Set probability",
        detail: "Probability (0–100) is required to keep pipeline useful.",
      });
  }

  if (stage === "proposal") {
    const amt =
      o.amount === null || o.amount === undefined || String(o.amount).trim() === "" ? null : Number(o.amount);
    if (amt === null || !Number.isFinite(amt) || amt <= 0)
      blockers.push({ key: "amount", title: "Set an amount", detail: "Amount must be set (> 0) before progressing." });
    if (!String(o.description || "").trim())
      blockers.push({
        key: "description",
        title: "Add a description",
        detail: "Add scope/summary so the opportunity is actionable and audit-safe.",
      });
  }

  if (stage === "negotiation") {
    if (!o.expected_close_date)
      blockers.push({
        key: "close_date",
        title: "Set expected close date",
        detail: "Expected close date is required for close-plan tracking.",
      });
    if (!o.primary_contact_id)
      blockers.push({
        key: "primary_contact",
        title: "Set a primary contact",
        detail: "Primary contact is required to close cleanly.",
      });
    if (o.probability === null || o.probability === undefined || String(o.probability).trim() === "")
      blockers.push({
        key: "probability",
        title: "Set probability",
        detail: "Probability (0–100) is required for close readiness.",
      });
  }

  if (stage === "closed") {
    if (status !== "won" && status !== "lost")
      blockers.push({
        key: "closed_requires_status",
        title: "Set Status to WON or LOST",
        detail: "Stage is CLOSED but Status is not WON/LOST. Fix status to satisfy DB coherence.",
      });
  }

  return blockers;
};

// ---------- Gold-standard tooltip (label-hover, portal, theme primary bg, multiline, no native title) ----------
function usePortalTooltip(theme) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const rafRef = useRef(null);

  const hide = useCallback(() => {
    setOpen(false);
    setText("");
  }, []);

  const onMove = useCallback((e) => {
    const x = e?.clientX || 0;
    const y = e?.clientY || 0;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setPos({ x, y }));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") hide();
    };
    const onScroll = () => hide();
    const onBlur = () => hide();

    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", onBlur);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [hide]);

  const tooltipNode = useMemo(() => {
    if (!open || !text) return null;

    const pad = 12;
    const maxW = 360;

    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;

    const left = Math.min(vw - pad, Math.max(pad, pos.x + 14));
    const top = Math.min(vh - pad, Math.max(pad, pos.y + 14));

    return createPortal(
      <div
        style={{
          position: "fixed",
          left,
          top,
          zIndex: 999999,
          pointerEvents: "none",
          background: theme?.primary_color || "#111827",
          color: "#fff",
          padding: "10px 12px",
          borderRadius: 12,
          boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
          fontSize: 12,
          fontWeight: 800,
          lineHeight: "16px",
          maxWidth: maxW,
          whiteSpace: "pre-line",
        }}
      >
        {text}
      </div>,
      document.body
    );
  }, [open, pos.x, pos.y, text, theme]);

  const bind = useMemo(
    () => ({
      onMouseEnter: (t) => {
        setText(String(t || ""));
        setOpen(true);
      },
      onMouseLeave: () => hide(),
      onMouseMove: onMove,
    }),
    [hide, onMove]
  );

  return { tooltipNode, bind };
}

export default function OpportunityStageProgressWidget({
  stages = [],
  opportunity = null,
  canEdit = false,
  onChangeStage,
  onChangeStatus,
}) {
  const { theme } = useTheme();
  const tooltip = usePortalTooltip(theme);

  const widgetRootRef = useRef(null);
  const mobilePagerRef = useRef(null);
  const stagePaneRef = useRef(null);
  const detailsPaneRef = useRef(null);

  // ✅ Always operate on canonical stage list (prevents "discovery" poisoning Mark Complete)
  const stagesCanon = useMemo(() => normalizeStages(stages), [stages]);

  const o = opportunity || {};
  const oStageCanon = useMemo(() => canonicalStage(o?.stage), [o?.stage]);

  const currentIdx = useMemo(() => idxOfStage(stagesCanon, oStageCanon), [stagesCanon, oStageCanon]);
  const currentStageKey = useMemo(
    () => (stagesCanon && stagesCanon[currentIdx] ? stagesCanon[currentIdx] : ""),
    [stagesCanon, currentIdx]
  );

  const blockers = useMemo(() => buildBlockers(stagesCanon, { ...o, stage: oStageCanon }), [stagesCanon, o, oStageCanon]);

  const stateByIdx = useMemo(() => {
    const out = [];
    for (let i = 0; i < (stagesCanon || []).length; i += 1) {
      if (i < currentIdx) out.push("complete");
      else if (i === currentIdx) out.push(blockers.length ? "blocked" : "current");
      else out.push("future");
    }
    return out;
  }, [stagesCanon, currentIdx, blockers.length]);

  const barBgSolid = "#F3F4F6";

  const wrap = useMemo(
    () => ({
      background: theme?.card_bg || "#fff",
      borderRadius: 14,
      padding: 14,
      boxShadow: theme?.card_shadow || "0 1px 2px rgba(0,0,0,0.06)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      minHeight: 0,
      ["--vx-primary"]: theme?.primary_color || "#0b2a3c",
      ["--vx-accent"]: theme?.accent_color || "#ff7a00",
      ["--vx-blocked"]: theme?.secondary_color || "#f59e0b",

      ["--vx-barbg"]: barBgSolid,

      ["--vx-future"]: "#E9EDF2",
      ["--vx-futureText"]: "rgba(17,24,39,0.62)",

      // geometry
      ["--vx-h"]: "52px",
      ["--vx-arrow"]: "18px",
      ["--vx-downArrow"]: "8px",
      ["--vx-gap"]: "1px",
      ["--vx-radius"]: "16px",
    }),
    [theme]
  );

  const status = norm(o?.status);

  const canMarkComplete = useMemo(() => {
    if (!canEdit) return false;
    if (!stagesCanon || stagesCanon.length === 0) return false;

    if (canonicalStage(currentStageKey) === "closed") return status === "won" || status === "lost";
    return blockers.length === 0;
  }, [canEdit, blockers.length, currentStageKey, stagesCanon, status]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const pager = mobilePagerRef.current;
    const stagePane = stagePaneRef.current;
    const detailsPane = detailsPaneRef.current;

    if (!root || !pager || !stagePane || !detailsPane) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    /*
     * Same host-resolution pattern proven by the responsive list widgets:
     * walk upward from the actual widget root and choose the first fixed-height
     * ancestor whose width still matches the widget. Do not assume that the
     * controlling mobile host is a react-grid-item.
     */
    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const naturalPaneHeight = (pane) => {
      if (!pane) return 0;

      const rectHeight = Math.ceil(
        pane.getBoundingClientRect().height || 0
      );

      const scrollHeight = Math.ceil(
        pane.scrollHeight || 0
      );

      return Math.max(rectHeight, scrollHeight);
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      /*
       * Critical: remove our own previous measurement before measuring again.
       * Otherwise the old --vx-mobile-pane-h becomes the measurement input and
       * the widget appears permanently fixed at its previous size.
       */
      pager.style.removeProperty("--vx-mobile-pane-h");
      pager.style.height = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const stageHeight = naturalPaneHeight(stagePane);
        const detailsHeight = naturalPaneHeight(detailsPane);

        const paneHeight = Math.ceil(
          Math.max(stageHeight, detailsHeight)
        );

        if (!Number.isFinite(paneHeight) || paneHeight <= 0) {
          return;
        }

        pager.style.setProperty(
          "--vx-mobile-pane-h",
          `${paneHeight}px`
        );

        /*
         * Keep the pager itself exactly one complete pane high. The second pane
         * then sits directly below it and is reached by the existing vertical
         * scroll/snap behaviour.
         */
        pager.style.height = `${paneHeight}px`;

        root.style.height = "auto";
        root.style.minHeight = "0px";

        /*
         * Second frame is deliberate. It lets the browser apply the measured
         * pager height first, then we read the true full widget/root geometry
         * including padding and WidgetWrapper offset.
         */
        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver) {
      resizeObserver.observe(stagePane);
      resizeObserver.observe(detailsPane);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.(
      "change",
      handleMediaChange
    );

    window.addEventListener(
      "resize",
      applyMobileSizing
    );

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.(
        "change",
        handleMediaChange
      );

      window.removeEventListener(
        "resize",
        applyMobileSizing
      );

      restoreHost();

      pager.style.removeProperty("--vx-mobile-pane-h");
      pager.style.height = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    blockers.length,
    canEdit,
    currentIdx,
    currentStageKey,
    stagesCanon.length,
    status,
  ]);

  const handleMarkComplete = useCallback(() => {
    if (!canEdit) return;

    if (canonicalStage(currentStageKey) === "closed") return;
    if (blockers.length > 0) return;

    const next = getNextStage(stagesCanon, currentIdx);
    if (!next) return;

    // ✅ always send canonical enum value
    if (onChangeStage) onChangeStage(canonicalStage(next));
  }, [blockers.length, canEdit, currentIdx, currentStageKey, onChangeStage, stagesCanon]);

  const onPickStage = useCallback(
    (targetStage, targetIdx) => {
      if (!canEdit || !onChangeStage) return;

      const targetCanon = canonicalStage(targetStage);

      if (targetIdx <= currentIdx) {
        onChangeStage(targetCanon);
        return;
      }

      if (blockers.length > 0) return;

      onChangeStage(targetCanon);
    },
    [blockers.length, canEdit, currentIdx, onChangeStage]
  );

  const stageTooltipText = useCallback(
    (s, i) => {
      const st = stateByIdx[i] || "future";
      const lines = [];

      lines.push(stageLabel(s));
      if (st === "complete") lines.push("Completed stage.");
      if (st === "current") lines.push("Current stage.");
      if (st === "blocked") lines.push("Current stage (blocked).");
      if (st === "future") lines.push("Upcoming stage.");

      if (!canEdit) lines.push("View-only.");
      else {
        if (i <= currentIdx) lines.push("Click to move to this stage.");
        else if (blockers.length > 0) lines.push("Blocked: fix items below before progressing.");
        else lines.push("Click to jump forward.");
      }

      return lines.join("\n");
    },
    [blockers.length, canEdit, currentIdx, stateByIdx]
  );

  const statusBtnStyle = useMemo(
    () => ({
      borderRadius: 999,
      padding: "8px 12px",
      fontWeight: 950,
      fontSize: 12,
      border: 0,
      cursor: canEdit ? "pointer" : "default",
      opacity: canEdit ? 1 : 0.55,
      userSelect: "none",
      whiteSpace: "nowrap",
    }),
    [canEdit]
  );

  const statusBtnClass = useCallback(
    (key) => {
      const active = status === key;
      const cls = active
        ? "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick"
        : "bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick";
      return `${cls} text-white`;
    },
    [status]
  );

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      style={wrap}
    >
      <style>{`
        .vx-stagebar {
          display: grid;
          grid-template-columns: 1fr 320px;
          grid-template-rows: auto auto;
          gap: 10px;
          align-items: start;
          min-height: 0;
        }

        .vx-detailsPane {
          display: contents;
        }

        @media (max-width: 1100px) {
          .vx-stagebar {
            grid-template-columns: 1fr;
            grid-template-rows: auto auto auto;
          }

          .vx-track,
          .vx-panel,
          .vx-actions {
            grid-column: 1;
            grid-row: auto;
          }
        }

        .vx-track {
          grid-column: 1;
          grid-row: 1;
          background: rgba(0,0,0,0.02);
          border-radius: 16px;
          padding: 12px;
          border: 1px solid rgba(0,0,0,0.08);
          overflow: hidden;
          min-height: 0;
        }

        .vx-stepsShell {
          background: var(--vx-barbg);
          border: 1px solid rgba(0,0,0,0.10);
          border-radius: var(--vx-radius);
          padding: 6px;
          overflow: hidden;
        }

        .vx-stepsWrap {
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
        }

        /* ✅ clip-path chevron segments with deterministic gap */
        .vx-steps {
          display: flex;
          align-items: center;
          height: var(--vx-h);
          background: var(--vx-barbg);
          border-radius: var(--vx-radius);
          padding: 3px;
          gap: var(--vx-gap);
          min-width: 640px;
          overflow: hidden;
          position: relative;
        }

        .vx-step {
          position: relative;
          flex: 1 1 0;
          height: calc(var(--vx-h) - 12px);
          display: flex;
          align-items: center;
          justify-content: center;

          font-weight: 950;
          font-size: 14px;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          user-select: none;
          white-space: nowrap;

          outline: none;
          cursor: default;

          clip-path: polygon(
            0% 0%,
            calc(100% - var(--vx-arrow)) 0%,
            100% 50%,
            calc(100% - var(--vx-arrow)) 100%,
            0% 100%,
            var(--vx-arrow) 50%
          );

          /* subtle segment outline like the reference */
          box-shadow: inset 0 0 0 1px rgba(17, 24, 39, 0.12);
        }

        .vx-step:first-child {
          clip-path: polygon(
            0% 0%,
            calc(100% - var(--vx-arrow)) 0%,
            100% 50%,
            calc(100% - var(--vx-arrow)) 100%,
            0% 100%
          );
          border-top-left-radius: var(--vx-radius);
          border-bottom-left-radius: var(--vx-radius);
        }

        .vx-step:last-child {
          clip-path: polygon(
            0% 0%,
            100% 0%,
            100% 100%,
            0% 100%,
            var(--vx-arrow) 50%
          );
          border-top-right-radius: var(--vx-radius);
          border-bottom-right-radius: var(--vx-radius);
        }

        .vx-complete { background: var(--vx-accent); color: #fff; }
        .vx-current { background: var(--vx-primary); color: #fff; }
        .vx-blocked { background: var(--vx-blocked); color: #fff; }
        .vx-future { background: var(--vx-future); color: var(--vx-futureText); }

        .vx-clickable { cursor: pointer; }
        .vx-clickable:hover { filter: brightness(1.04); }
        .vx-clickable:active { filter: brightness(1.0); }

        .vx-step:focus-visible {
          box-shadow: 0 0 0 3px rgba(59,130,246,0.35);
        }

        .vx-actions {
          grid-column: 2;
          grid-row: 1 / span 2;
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-height: 0;
        }

        .vx-mark {
          border: 0;
          border-radius: 12px;
          padding: 12px 14px;
          font-weight: 950;
          font-size: 13px;
          cursor: pointer;
          color: #fff;
          background: var(--vx-accent);
          opacity: 1;
          width: 100%;
        }
        .vx-mark:disabled { cursor: default; opacity: 0.45; }

        .vx-statusRow {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        /* Mobile: compact vertical progress, then natural vertical snap to details */
        @media (max-width: 767px) {
          .vx-stagebar {
            display: block;
            height: var(--vx-mobile-pane-h, auto);
            max-height: none;
            overflow-x: hidden;
            overflow-y: auto;
            scroll-snap-type: y mandatory;
            scroll-behavior: smooth;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }

          .vx-stagebar::-webkit-scrollbar {
            display: none;
          }

          .vx-track,
          .vx-detailsPane {
            width: 100%;
            min-width: 0;
            min-height: var(--vx-mobile-pane-h, 0px);
            box-sizing: border-box;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-track {
            display: block;
            padding: 8px;
            overflow: visible;
          }

          .vx-detailsPane {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 8px;
          }

          .vx-stepsShell {
            padding: 4px;
            overflow: visible;
          }

          .vx-stepsWrap {
            overflow: visible;
          }

          .vx-steps {
            flex-direction: column;
            align-items: stretch;
            width: 100%;
            min-width: 0;
            height: auto;
            padding: 2px;
            gap: 3px;
            overflow: visible;
          }

          .vx-step {
            box-sizing: border-box;
            flex: 0 0 auto;
            width: 100%;
            height: 48px;
            min-height: 48px;
            padding: 10px 12px 12px;
            font-size: 11px;
            line-height: 14px;
            letter-spacing: 0.15px;
            white-space: normal;
            text-align: center;

            clip-path: polygon(
              0% 0%,
              50% var(--vx-downArrow),
              100% 0%,
              100% calc(100% - var(--vx-downArrow)),
              50% 100%,
              0% calc(100% - var(--vx-downArrow))
            );
          }

          .vx-step:first-child {
            padding-top: 8px;
            padding-bottom: 14px;

            clip-path: polygon(
              0% 0%,
              100% 0%,
              100% calc(100% - var(--vx-downArrow)),
              50% 100%,
              0% calc(100% - var(--vx-downArrow))
            );

            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            border-bottom-left-radius: 0;
          }

          .vx-step:last-child {
            padding-top: 14px;
            padding-bottom: 8px;

            clip-path: polygon(
              0% 0%,
              50% var(--vx-downArrow),
              100% 0%,
              100% 100%,
              0% 100%
            );

            border-top-right-radius: 0;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
          }

          .vx-panel {
            margin-top: 0;
            padding: 10px;
            border-radius: 12px;
          }

          .vx-blockTitle {
            font-size: 12px;
            margin-bottom: 6px;
          }

          .vx-blockItem {
            padding: 8px;
            border-radius: 10px;
            margin-bottom: 6px;
          }

          .vx-blockItemH {
            font-size: 11px;
            margin-bottom: 3px;
          }

          .vx-blockItemP {
            font-size: 11px;
            line-height: 14px;
          }

          .vx-actions {
            gap: 8px;
          }

          .vx-mark {
            min-height: 44px;
            padding: 9px 10px;
            font-size: 11px;
            border-radius: 10px;
          }

          .vx-statusRow {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 6px;
          }

          .vx-statusRow > button {
            width: 100%;
            min-width: 0;
            min-height: 44px;
            padding: 7px 8px !important;
            font-size: 11px !important;
            white-space: normal !important;
          }

          .vx-blockItemH,
          .vx-blockItemP,
          .vx-meta {
            overflow-wrap: anywhere;
          }

          .vx-meta {
            font-size: 11px;
            text-align: left;
          }
        }

        .vx-panel {
          grid-column: 1;
          grid-row: 2;
          margin-top: 2px;
          border-radius: 14px;
          padding: 12px;
          background: rgba(255,255,255,0.55);
          border: 1px solid rgba(0,0,0,0.06);
          backdrop-filter: blur(2px);
        }

        .vx-blockTitle {
          font-weight: 950;
          font-size: 13px;
          margin-bottom: 8px;
          color: #b91c1c;
        }

        .vx-blockItem {
          padding: 10px 10px;
          border-radius: 12px;
          background: #fff;
          border: 1px solid rgba(0,0,0,0.06);
          margin-bottom: 8px;
        }
        .vx-blockItem:last-child { margin-bottom: 0; }

        .vx-blockItemH { font-weight: 950; font-size: 12px; margin-bottom: 4px; }
        .vx-blockItemP { font-size: 12px; opacity: 0.75; line-height: 16px; }

        .vx-meta { font-size: 12px; opacity: 0.75; text-align: right; margin-top: 2px; }
      `}</style>

      {tooltip.tooltipNode}

      <div ref={mobilePagerRef} className="vx-stagebar">
        <div ref={stagePaneRef} className="vx-track">
          <div className="vx-stepsShell">
            <div className="vx-stepsWrap">
              <div className="vx-steps">
                {(stagesCanon || []).map((s, i) => {
                  const st = stateByIdx[i] || "future";
                  const cls =
                    st === "complete"
                      ? "vx-step vx-complete"
                      : st === "current"
                      ? "vx-step vx-current"
                      : st === "blocked"
                      ? "vx-step vx-blocked"
                      : "vx-step vx-future";

                  const isClickable = !!canEdit;

                  return (
                    <div
                      key={String(s)}
                      className={cls + (isClickable ? " vx-clickable" : "")}
                      onClick={() => onPickStage(s, i)}
                      onMouseEnter={() => tooltip.bind.onMouseEnter(stageTooltipText(s, i))}
                      onMouseLeave={tooltip.bind.onMouseLeave}
                      onMouseMove={tooltip.bind.onMouseMove}
                      role={isClickable ? "button" : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (!isClickable) return;
                        if (e.key === "Enter" || e.key === " ") onPickStage(s, i);
                      }}
                    >
                      {stageLabel(s)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div ref={detailsPaneRef} className="vx-detailsPane">
          <div className="vx-panel">
            {blockers.length > 0 ? (
              <>
                <div className="vx-blockTitle">Fix required</div>
                {blockers.map((b) => (
                  <div key={b.key} className="vx-blockItem">
                    <div className="vx-blockItemH">{b.title}</div>
                    <div className="vx-blockItemP">{b.detail}</div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <div style={{ fontWeight: 950, fontSize: 12, marginBottom: 4 }}>Next step</div>
                <div style={{ fontSize: 12, lineHeight: "16px", opacity: 0.75 }}>
                  {canonicalStage(currentStageKey) === "closed"
                    ? "Closed stage. Set Status to WON or LOST to complete."
                    : "Stage is clear. Use “Mark stage complete” to progress."}
                </div>
              </>
            )}
          </div>

          <div className="vx-actions">
            <button
              type="button"
              className="vx-mark"
              onClick={handleMarkComplete}
              disabled={!canMarkComplete}
              onMouseEnter={() => {
                const txt = !canEdit
                  ? "View-only.\nYou do not have edit access."
                  : canonicalStage(currentStageKey) === "closed"
                  ? "Closed stage completion is status-based.\nSet Status to WON or LOST."
                  : blockers.length
                  ? "Blocked.\nFix required items before progressing."
                  : "Marks the current stage complete\nand moves to the next stage.";
                tooltip.bind.onMouseEnter(txt);
              }}
              onMouseLeave={tooltip.bind.onMouseLeave}
              onMouseMove={tooltip.bind.onMouseMove}
            >
              Mark stage complete
            </button>

            <div className="vx-statusRow">
              {["open", "on_hold", "won", "lost"].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={statusBtnClass(s)}
                  style={statusBtnStyle}
                  onClick={() => canEdit && onChangeStatus && onChangeStatus(s)}
                  disabled={!canEdit}
                  onMouseEnter={() =>
                    tooltip.bind.onMouseEnter(`Status: ${titleCase(s)}\n${canEdit ? "Click to set status." : "View-only."}`)
                  }
                  onMouseLeave={tooltip.bind.onMouseLeave}
                  onMouseMove={tooltip.bind.onMouseMove}
                >
                  {titleCase(s)}
                </button>
              ))}
            </div>

            <div className="vx-meta">
              {canEdit ? "Editable" : "View-only"} · #{o?.id ? String(o.id) : "—"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
